/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo } from 'react';
import { FormSection, FormRow } from '@tanium/react-form';
import { Field, FieldProps, FormikErrors, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { FormSelectInput } from '@tanium/react-form-select-input';
import get from 'lodash.get';
import { useQueryParam, StringParam } from 'use-query-params';
import { useDispatch, useSelector } from 'react-redux';
import { BarsTransition } from '@tanium/react-loading-transition';
import { RequestTargetType } from 'gql/Request';
import { ACCESS_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { ACCESS_TYPES } from 'components/pages/request/CreateRequestPage';
import TargetTypeSelectors from 'components/pages/console/store/TargetTypeSelectors';
import TargetTypesSlice from 'components/pages/console/store/TargetTypeSlice';
import { BASTION_TYPE } from 'components/pages/request/requestPartials/createRequestFieldsPartials/accessTypePartials/BastionAccess';
import { NAMESPACE, ROLE } from './accessTypePartials/KubernetesRoleAccess';
import { API_ACCESS_TYPE } from './accessTypePartials/KubernetesAPIAccess';

export const TARGET_ID = 'targetId';
export const TARGET_NAME = 'targetName';
export const TARGET_TYPE = 'targetType';

// This page manages the form to create a single request
export const Environments = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formik = useFormikContext();
  const [targetTypeQuery, setTargetTypeQuery] = useQueryParam(TARGET_TYPE, StringParam);
  const [__, setAccessTypeQuery] = useQueryParam(ACCESS_TYPE, StringParam);
  const [_, setTargetIdQuery] = useQueryParam(TARGET_ID, StringParam);
  const targetTypes = useSelector(TargetTypeSelectors.selectAvailableTargetTypes);
  const targetTypesProcessing = useSelector(TargetTypeSelectors.selectTargetTypesProcessing);
  const targetTypesTouched = useSelector(TargetTypeSelectors.selectTargetTypesTouched);

  const formInputIdPrefix = 'create-request-';

  const environmentDropdownList = useMemo(() => {
    return [
      { label: t('AWS_ACCOUNT'), value: RequestTargetType.AWSAccount },
      { label: t('MS_ENTRA_ID'), value: RequestTargetType.MsEntraID },
      { label: t('CUSTOMER_ENVIRONMENT'), value: RequestTargetType.CustomerEnvironment },
      { label: t('EOS_SERVICE'), value: RequestTargetType.EosService },
      { label: t('GLOBAL_SERVICE'), value: RequestTargetType.GlobalService },
      { label: t('KUBERNETES_CLUSTER'), value: RequestTargetType.KubernetesCluster },
    ].filter((x) => targetTypes.includes(x.value));
  }, [t, targetTypes]);

  useEffect(() => {
    if (!targetTypesProcessing && !targetTypesTouched) {
      dispatch(TargetTypesSlice.actions.fetchAvailableTargetTypes());
    }
  }, [dispatch, targetTypesProcessing, targetTypesTouched]);

  // Initializing targetType value
  useEffect(() => {
    if (targetTypeQuery && targetTypesTouched && !targetTypesProcessing) {
      void formik.setFieldValue(TARGET_TYPE, targetTypeQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetTypesTouched, targetTypesProcessing]);

  return targetTypesProcessing ? (
    <div data-testid="bars-loading">
      <BarsTransition isLoading={targetTypesProcessing} />
    </div>
  ) : (
    <FormSection title={t('ENVIRONMENT_TYPE')}>
      <Field name={TARGET_TYPE} data-testid={TARGET_TYPE}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('SELECT_ENVIRONMENT_TYPE')}
            description={t('SELECT_ENVIRONMENT_TYPE_INFO')}
            required
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
          >
            <FormSelectInput
              onChange={(option): void => {
                const { value } = option;
                void form.setFieldValue(TARGET_TYPE, value);
                void form.setFieldValue(TARGET_ID, undefined);
                void form.setFieldValue(ACCESS_TYPE, undefined);
                void form.setFieldValue(ROLE, undefined);
                void form.setFieldValue(API_ACCESS_TYPE, undefined);
                void form.setFieldValue(NAMESPACE, undefined);
                void form.setFieldValue(BASTION_TYPE, undefined);
                void form.setFieldValue(ACCESS_TYPES, []);
                setTargetTypeQuery(value);
                setTargetIdQuery(undefined);
                setAccessTypeQuery(undefined);
              }}
              value={environmentDropdownList.find((option) => option.value === field.value)}
              inputId={`${formInputIdPrefix}${field.name}`}
              options={environmentDropdownList}
              aria-label={t('ENVIRONMENT_TYPE')}
            />
          </FormRow>
        )}
      </Field>
    </FormSection>
  );
};

export default Environments;
